import React, { useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Heading3 } from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder, className = '' }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertHeading = (level) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const heading = document.createElement(`h${level}`);
    
    try {
      range.surroundContents(heading);
    } catch (e) {
      heading.innerHTML = range.toString();
      range.deleteContents();
      range.insertNode(heading);
    }
    
    editorRef.current?.focus();
    handleInput();
  };

  return (
    <div className={`rich-text-editor ${className}`}>
      {/* Toolbar - increased touch targets for accessibility (44x44px minimum) */}
      <div className="toolbar flex flex-wrap gap-1 p-2 border border-border rounded-t-lg bg-background" role="toolbar" aria-label="Text formatting options">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertHeading(1)}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Heading 1"
        >
          <Heading1 size={18} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertHeading(2)}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Heading 2"
        >
          <Heading2 size={18} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertHeading(3)}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Heading 3"
        >
          <Heading3 size={18} aria-hidden="true" />
        </Button>
        <div className="w-px h-11 bg-border mx-1" aria-hidden="true" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('bold')}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Bold"
        >
          <Bold size={18} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('italic')}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Italic"
        >
          <Italic size={18} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('underline')}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Underline"
        >
          <Underline size={18} aria-hidden="true" />
        </Button>
        <div className="w-px h-11 bg-border mx-1" aria-hidden="true" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('insertUnorderedList')}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Bullet list"
        >
          <List size={18} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('insertOrderedList')}
          className="h-11 w-11 p-0 touch-target"
          aria-label="Numbered list"
        >
          <ListOrdered size={18} aria-hidden="true" />
        </Button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder || "Rich text editor"}
        className="editor min-h-[200px] p-4 border border-t-0 border-border rounded-b-lg bg-background focus:outline-2 focus:outline-ring text-base md:text-sm"
        data-placeholder={placeholder}
        style={{
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}
      />

      <style>{`
        .rich-text-editor .editor:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          position: absolute;
        }
        .rich-text-editor .editor h1 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-editor .editor h2 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-editor .editor h3 {
          font-size: 1.1em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-editor .editor p {
          margin: 0.5em 0;
        }
        .rich-text-editor .editor ul,
        .rich-text-editor .editor ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
        .rich-text-editor .editor li {
          margin: 0.25em 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
